import React, { ChangeEvent, FormEvent } from 'react'
import {
  Body2,
  Box,
  Button,
  Color,
  Display,
  FlexDirection,
  FormFieldStatus,
  InputField,
  Spinner,
} from '@gousto-internal/citrus-react'

import type { LoggedOutBoxSummaryActions, LoggedOutBoxSummaryPostcodeView } from '../types'

type PostcodePanelProps = {
  view: LoggedOutBoxSummaryPostcodeView
  actions: Pick<LoggedOutBoxSummaryActions, 'onPostcodeChange' | 'onPostcodeContinue'>
}

const INTRO_COPY = 'We deliver up to 7 days a week depending on where you live.'
const POSTCODE_HELPER_COPY =
  'Your postcode will help us figure out which delivery slots are available in your area'
const POSTCODE_INPUT_ID = 'logged-out-box-summary-postcode'

export const PostcodePanel = ({ view, actions }: PostcodePanelProps) => {
  const fieldStatus = view.validationMessage ? FormFieldStatus.Error : undefined
  const isSubmitDisabled = view.continueCta.isDisabled || view.continueCta.isPending

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (isSubmitDisabled) {
      return
    }

    actions.onPostcodeContinue()
  }

  const handlePostcodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    actions.onPostcodeChange(event.target.value)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={6}
        paddingH={3}
        paddingBottom={3}
        width="100%"
      >
        <Body2 color={Color.ColdGrey_800}>{INTRO_COPY}</Body2>

        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3} width="100%">
          <InputField
            id={POSTCODE_INPUT_ID}
            label={view.title}
            value={view.postcode}
            placeholder=""
            autoComplete="postal-code"
            minLength={view.constraints.minLength}
            maxLength={view.constraints.maxLength}
            status={fieldStatus}
            validationMessage={view.validationMessage ?? undefined}
            onChange={handlePostcodeChange}
            data-testid="logged-out-box-summary-postcode-input"
          />
          <Body2 color={Color.ColdGrey_800}>{POSTCODE_HELPER_COPY}</Body2>
        </Box>

        <Button
          type="submit"
          width="100%"
          disabled={isSubmitDisabled}
          aria-busy={view.continueCta.isPending}
          data-testid="logged-out-box-summary-postcode-submit"
        >
          {view.continueCta.isPending ? (
            <Spinner color={Color.White} size={6} />
          ) : (
            view.continueCta.label
          )}
        </Button>
      </Box>
    </form>
  )
}
